import PageHeader from "../components/PageHeader";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageProvider";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxmb3VudGFpbiUyMHBlbiUyMHNpZ25pbmclMjBkb2N1bWVudCUyMGVsZWdhbnR8ZW58MHx8fHwxNzg0MDY1MDY0fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t } = useLang();
  return (
    <>
      <PageHeader
        eyebrow={t("page.about.eyebrow")}
        title={t("page.about.title")}
        lede={t("page.about.lede")}
      />

      {/* KEY STATS */}
      <section className="pb-8 lg:pb-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 border-y border-[var(--bw-border)] py-10">
            {[
              { k: "S$2.1B+", v: "Advised & invested capital" },
              { k: "Since 2022", v: "Year of foundation" },
              { k: "12", v: "Principal families" },
              { k: "202213354N", v: "UEN, Republic of Singapore" },
            ].map((s, i) => (
              <div key={s.k} data-testid={`about-stat-${i}`}>
                <div className="font-serif-display text-3xl lg:text-4xl text-gold mb-2">{s.k}</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-muted">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 space-y-8">
            <div className="eyebrow">Origin</div>
            <p className="text-white/85 text-lg leading-relaxed">
              Bonworld Family Office was formed in Singapore in 2022 to serve a small community of
              principal families with cross-border interests across Europe, Greater China and
              South-East Asia. From our office at Peninsula Plaza we operate as a discreet
              coordinating centre — investment committee, secretariat and counsel — for the
              families we hold in trust. Today, we advise and invest more than S$2.1 billion of
              family capital under a single, aligned mandate.
            </p>
            <p className="text-muted leading-relaxed">
              We are not a bank. We do not sell products. We accept no retrocessions. Every hour
              of our work, and every basis point of return, is aligned with a single principal
              family whose interests we serve.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8 border-t border-[var(--bw-border)]">
              <div>
                <div className="eyebrow mb-3">Values</div>
                <ul className="space-y-3 text-white/85">
                  <li>· Discretion above visibility</li>
                  <li>· Alignment above activity</li>
                  <li>· Patience above performance</li>
                  <li>· Family above firm</li>
                </ul>
              </div>
              <div>
                <div className="eyebrow mb-3">What we do not do</div>
                <ul className="space-y-3 text-muted">
                  <li>· Public marketing</li>
                  <li>· Retail products</li>
                  <li>· Kick-backs or retrocessions</li>
                  <li>· Short-horizon speculation</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Fountain pen resting on notebook"
                className="w-full h-[560px] object-cover"
                style={{ filter: "brightness(0.75)" }}
              />
              <div className="absolute -bottom-6 -left-6 bg-surface border border-[var(--bw-border)] p-6 max-w-xs">
                <div className="text-gold font-serif-display text-2xl">Peninsula Plaza</div>
                <div className="text-xs tracking-[0.28em] uppercase text-muted mt-1">Singapore 179098</div>
                <div className="text-sm text-white/70 mt-3 leading-relaxed">
                  Our office sits within the quiet financial spine of the Civic District — steps
                  from the Supreme Court, MAS and the historic riverbank.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--bw-border)] py-24 lg:py-28 bg-surface">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-6">Structure</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-white leading-tight">
              A licensed, regulated Singapore office.
            </h2>
            <p className="text-muted text-sm mt-6 leading-relaxed">
              Bonworld Family Office Pte. Ltd. · UEN 202213354N · Incorporated 2022 · Republic of Singapore.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { t: "Jurisdiction", d: "Incorporated in the Republic of Singapore. Governed by MAS-aligned best practice." },
              { t: "Confidentiality", d: "Client identities and portfolios never leave the four walls of the office." },
              { t: "Custody", d: "Assets held with tier-one global custodians in the family's own name." },
              { t: "Independence", d: "No product manufacturing. No affiliated funds. Full open architecture." },
            ].map((c) => (
              <div key={c.t} className="card-lux">
                <div className="text-gold text-sm tracking-[0.28em] uppercase mb-3">{c.t}</div>
                <div className="text-white/80 text-sm leading-relaxed">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <div className="eyebrow justify-center mb-6" style={{ display: "inline-flex" }}>Next</div>
          <h3 className="font-serif-display text-3xl lg:text-4xl text-white leading-tight">
            Read how we deploy capital — <Link to="/philosophy" className="text-gold italic underline decoration-1 underline-offset-8">our philosophy</Link>.
          </h3>
        </div>
      </section>
    </>
  );
}
